import { useRoundHref } from "@client/RoundRouteContext";
import { navigateInApp } from "@client/lib/navigation/navigateInApp";
import { useActingAsUserId } from "@client/pages/round/round-context";
import { getOpenAppDrawer, useAppDrawer } from "@client/utils/exclusiveDrawer";
import {
  isBooleanRecord,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  useLocalStorage,
} from "@sfc/ui";
import { useRef } from "react";
import { TodoDrawerContent } from "./TodoDrawerContent";

type TodoExpansionState = Record<string, boolean>;

const EMPTY_EXPANSION: TodoExpansionState = {};

/**
 * The To Do rail — the funder's ordered walkthrough of a round, as a right-hand
 * drawer beside the Queue.
 *
 * Mounted inside the round subtree (next to the General Round Notes drawer)
 * rather than in the app bar, because the rail is derived largely from the
 * round aggregate, which only exists in here. The app-bar trigger lives in
 * `TodoDrawerTrigger` and reaches this through the shared drawer store — the
 * same bridge the General Notes drawer uses.
 */
export function TodoDrawer({ roundId }: { roundId: string }) {
  const [open, setOpen] = useAppDrawer("todo");
  const actingAsUserId = useActingAsUserId();
  // The drawer content unmounts while closed so its round queries stay lazy.
  // Expansion is interaction state, though, and must outlive that gate: opening
  // a video closes the drawer, so keeping this in TodoDrawerBody reset every
  // track to the derived default as soon as the funder came back.
  // Persist explicit choices per viewer and round too, so a reload does not
  // reset their working context. The hook also follows key changes if either
  // scope changes without remounting this drawer.
  const [expanded, setExpanded] = useLocalStorage<TodoExpansionState>(
    `todoRailExpansion:v1:${actingAsUserId}:${roundId}`,
    EMPTY_EXPANSION,
    { validate: isBooleanRecord },
  );
  const roundHref = useRoundHref();
  const pendingNavigationRef = useRef<string | null>(null);

  const closeThenNavigate = (href: string) => {
    pendingNavigationRef.current = roundHref(href);
    setOpen(false);
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        // Reopening supersedes a destination left by an interrupted close.
        if (nextOpen) {
          pendingNavigationRef.current = null;
        }

        setOpen(nextOpen);
      }}
      onOpenChangeComplete={(nextOpen) => {
        if (nextOpen) {
          return;
        }

        const href = pendingNavigationRef.current;
        pendingNavigationRef.current = null;

        // A sibling drawer (or a rapid reopen) took the shared slot while this
        // sheet was exiting. Do not let the stale close navigate underneath it.
        if (href == null || getOpenAppDrawer() != null) {
          return;
        }

        // An explicit repeat click on the same object link should re-scroll
        // and pulse it even though the URL itself is already at that target.
        navigateInApp(href, { retriggerHash: true });
      }}
    >
      <SheetContent
        side="right"
        className="w-95 sm:max-w-105"
        // Base UI restores focus to the drawer trigger after close completion.
        // A navigation close owns the next focus target instead (for example,
        // Overall Score), so let that destination keep focus once it mounts.
        finalFocus={pendingNavigationRef.current == null ? undefined : false}
      >
        <SheetHeader>
          <SheetTitle>To Do</SheetTitle>
        </SheetHeader>

        {/* Everything that reads the round aggregate lives below this gate.
            The drawer is rendered by the round shell on every route, so a
            subscription up here would re-render it on every action the round
            receives — for a panel nobody has opened. */}
        {open && (
          <TodoDrawerContent
            roundId={roundId}
            onNavigate={closeThenNavigate}
            expanded={expanded}
            setExpanded={setExpanded}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
